import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildCsvRules, CSV_RULES_FILENAME } from './markerCsvRules';
import './CsvRulesModal.css';

// Shows the CSV import rules as one Markdown text the admin copies into a
// language model's prompt. The text is built from the same constants the
// import and the export use (markerCsvRules.js), so it never goes stale.

export default function CsvRulesModal({ open, onClose }) {
  const text = useMemo(() => buildCsvRules(), []);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      // Older browsers or a non-secure origin: select the text so Ctrl+C works.
      textareaRef.current?.select();
      console.error('Zwischenablage nicht verfügbar:', err);
    }
  };

  const download = () => {
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = CSV_RULES_FILENAME;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="csvr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="csvr-dialog" role="dialog" aria-modal="true" aria-labelledby="csvr-title">
        <header className="csvr-header">
          <div>
            <h3 id="csvr-title" className="csvr-title">CSV-Regeln für den Import</h3>
            <p className="csvr-sub">
              Alle Spalten, Pflichtfelder, erlaubten Werte und Kombinationen als Markdown, zum Einfügen in einen LLM-Prompt.
              Erzeugt aus dem Code des Imports, also immer aktuell.
            </p>
          </div>
          <button type="button" className="csvr-close" onClick={onClose} aria-label="Dialog schließen">×</button>
        </header>

        <div className="csvr-actions">
          <button type="button" className="btn btn-primary" onClick={copy}>{copied ? 'Kopiert ✓' : 'In die Zwischenablage kopieren'}</button>
          <button type="button" className="btn" onClick={download}>Als .md herunterladen</button>
          <span className="csvr-hint">{text.length.toLocaleString('de-DE')} Zeichen</span>
        </div>

        <textarea ref={textareaRef} className="csvr-text" value={text} readOnly spellCheck={false} aria-label="CSV-Regeln" />
      </div>
    </div>
  );
}
